import React, { useState } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import config from 'react-global-configuration';
import { useSelector } from 'react-redux';

import { CoreLink, Paragraph, View } from '@uncinc/react-drupal-core';

import {
  Button,
  Container,
  Picture,
} from '@uncinc/uncinc-react-kitchen-sink';

import { WysiwygField, SwirlIllustrations, NewsletterBlock } from '../../../components';
import HomepageSearchLazy from '../../../components/HomepageSearch/index.lazy';
import {
  SCREEN_TABLET,
  SCREEN_MOBILE,
  IS_CLIENT,
  DOMAIN_VNL,
  DOMAIN_KVNL,
  PARALLAX_ANIMATION_SPEED_FULLPAGE,
} from '../../../config/constants';

import './style.scss';

const NodeHomePage = ({ entity, className }) => {
  const classes = classNames(className, 'NodeHomePage', {
    NodeHomePage__no_hero: !entity.getIn(['field_hero_image', 0]),
  });

  const media = entity.getIn(['field_hero_image', 0], Immutable.Map()).toJS();
  const mediaMobile = entity.getIn(
    ['field_media_item_mobile', 0], entity.getIn(['field_hero_image', 0], Immutable.Map()),
  ).toJS();

  const themeTitle = entity.getIn(['field_theme_title', 0, 'value'], null);
  const mediaIsImage = media.bundle && media.bundle === 'image';
  const mediaIsVideo = media.source && media.bundle && media.bundle === 'video';
  const mediaMobileIsImage = mediaMobile.bundle && mediaMobile.bundle === 'image';
  const mediaMobileIsVideo = mediaMobile.source && mediaMobile.bundle && mediaMobile.bundle === 'video';

  const desktopVideoMediaThumbnail = entity.getIn(['field_hero_image', 0, 'entity', 'field_media_image', 0, 'styles', 'hero_desktop'], null);
  const mobileVideoMediaThumbnail = entity.getIn(['field_media_item_mobile', 0, 'entity', 'field_media_image', 0, 'styles', 'hero_mobile'], null);

  const heroClassName = classNames('NodeHomePage__hero', mediaIsImage ? 'NodeHomePage__hero__hasImage' : '');

  const linkTitle = entity.getIn(['field_link', 0, 'title']);
  const linkUri = entity.getIn(['field_link', 0, 'alias'], entity.getIn(['field_link', 0, 'uri'], null));
  const secondaryLinkTitle = entity.getIn(['field_link_secondary', 0, 'title']);
  const secondaryLinkUri = entity.getIn(['field_link_secondary', 0, 'alias'], entity.getIn(['field_link_secondary', 0, 'uri'], null));

  const deviceSizeDetected = useSelector((state) => state.getIn(['uiInternal', 'deviceSize']).size > 0);
  const isMobile = useSelector((state) => state.getIn(['uiInternal', 'deviceSize', 'isMobile'], null) === true);
  const [videoIsPlaying, setVideoIsPlaying] = useState(true);

  const scrollToContent = () => window.location.hash = 'main';

  const toggleHeroVideoPlaying = () => {
    if (videoIsPlaying) {
      document.getElementById('hero-video').pause();
    } else {
      document.getElementById('hero-video').play();
    }

    setVideoIsPlaying(!videoIsPlaying);
  };

  return (
    <article typeof="schema:WebPage" className={classes}>
      <div className={heroClassName}>
        {entity.getIn(['field_hero_image', 0]) && (
          <div className="NodeHomePage__hero__image__wrapper">
            { /* eslint-disable */}
            {mediaIsVideo && mediaMobileIsVideo && (
              <>
                <div className="NodeHomePage__hero__image">
                  {mobileVideoMediaThumbnail && desktopVideoMediaThumbnail && (
                    <Picture
                      className="video__thumbnail__preload"
                      src={{
                        [SCREEN_MOBILE]: mobileVideoMediaThumbnail,
                        [SCREEN_TABLET]: desktopVideoMediaThumbnail
                      }}
                    />
                  )}
                  {IS_CLIENT && deviceSizeDetected && (
                    <>
                      {isMobile && (
                        <video id="hero-video" className="mobile__video" controls={false} poster={mobileVideoMediaThumbnail} muted loop playsInline autoPlay>
                          <source src={mediaMobile.source} type="video/mp4" />
                        </video>
                      )}

                      {!isMobile && (
                        <video id="hero-video" className="desktop__video" controls={false} poster={desktopVideoMediaThumbnail} muted loop playsInline autoPlay>
                          <source src={media.source} type="video/mp4" />
                        </video>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
            {mediaIsImage && mediaMobileIsImage && (
              <Picture
                src={{
                  [SCREEN_MOBILE]: mediaMobile.styles['hero_mobile'],
                  [SCREEN_TABLET]: media.styles['hero_desktop']
                }}
              />
            )}
          </div>
        )}

        <Container className="NodeHomePage__hero__title">
          <div className="row">
            <div className="col-10">
              <div className="NodeHomePage__info">
                <WysiwygField
                  property="schema:text"
                  className="NodeHomePage__body"
                  /* Below we deliberately use the value here so VNL can use html */
                  html={entity.getIn(['body', 0, 'value'], '')}
                />
                <div className="NodeHomePage__info__links">
                  {linkUri && (
                    <CoreLink className="Button Button--primary" to={linkUri}>{linkTitle}</CoreLink>
                  )}
                  {secondaryLinkUri && (
                    <CoreLink className="Button Button--secondary" to={secondaryLinkUri}>{secondaryLinkTitle}</CoreLink>
                  )}
                </div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-2  NodeHomePage__hero__buttons">
              {config.get('CURRENT_DOMAIN') === DOMAIN_VNL && (
                <Button primary={false} onClick={toggleHeroVideoPlaying} className="NodeHomePage__video-pause-button">
                  {videoIsPlaying ? <span>&#x23F8;</span> : <span>&#x23F5;</span>}
                  <span className="visually-hidden">Pauzeer video</span>
                </Button>
              )}
              <Button primary={false} onClick={scrollToContent} className="NodeHomePage__down-button">
                <span className="icon icon-arrow-right" />
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container className="NodeHomePage__vocabularyTerms">
        <div id={config.get('CURRENT_DOMAIN') === DOMAIN_VNL && 'main'}>
          <View id="vocabulary_terms" display="themes" title={themeTitle} />
        </div>
      </Container>

      <Container>
        <div className="row" id={config.get('CURRENT_DOMAIN') === DOMAIN_KVNL && 'main'}>
          <div className="col-lg-12">
            {/* Don't load component lazy because of cls */}
            <HomepageSearchLazy entity={entity} />
          </div>
        </div>
      </Container>

      {entity.get('field_paragraphs') ? (
        <div className="NodeHomePage__paragraphs">
          {entity.get('field_paragraphs', Immutable.List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
            return (
              <div key={`paragraph:${id}:${language}`} id={id}>
                <Paragraph
                  id={id}
                  entity={paragraph.get('entity')}
                />
              </div>
            );
          })}
        </div>
      ) : null}

      <SwirlIllustrations
        variant="fullpage"
        parallaxSpeed={PARALLAX_ANIMATION_SPEED_FULLPAGE}
      />

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </article>
  );
};

export default NodeHomePage;
